
import React from 'react';
import { User, LogOut, Home, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getUserInitials } from './utils';

interface MobileNavProps {
  navLinks: Array<{ name: string; href: string }>;
  mobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ 
  navLinks, 
  mobileMenuOpen, 
  toggleMobileMenu 
}) => {
  const { user, isGuest, signOut, convertGuestToFull } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getIconForLink = (name: string) => {
    switch (name.toLowerCase()) {
      case 'home':
        return <Home className="w-6 h-6 text-white" />;
      case 'my trips':
        return <User className="w-6 h-6 text-white" />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex items-center">
        {user && (
          <Avatar className="h-8 w-8 mr-3">
            <AvatarImage src={user.user_metadata?.avatar_url} />
            <AvatarFallback className="bg-purple-DEFAULT/80 text-white">
              {getUserInitials(user, isGuest)}
            </AvatarFallback>
          </Avatar>
        )}
        <button
          onClick={toggleMobileMenu}
          className="text-white p-2 focus:outline-none"
          aria-label="Toggle Menu"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {mobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12H21M3 6H21M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
      </div>

      {/* Menu - Only visible when mobileMenuOpen is true */}
      {mobileMenuOpen && (
        <div 
          className={cn(
            "fixed inset-0 top-16 bg-purple-dark z-40 transition-transform duration-300 flex flex-col",
            mobileMenuOpen ? "translate-y-0" : "-translate-y-full"
          )}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <div className="h-full w-full px-4 py-6 shadow-lg">
            <nav className="flex flex-col px-2 py-4 space-y-6 w-full">
              {/* Only show navLinks here, don't duplicate "My Trips" */}
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="flex items-center text-white font-semibold px-4 py-3 rounded-md hover:bg-purple-tertiary/30 bg-purple-secondary/70 shadow-md"
                  onClick={() => toggleMobileMenu()}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {getIconForLink(link.name)}
                  <span className="ml-3 text-xl">{link.name}</span>
                </a>
              ))}
              
              {user ? (
                <>
                  <button 
                    onClick={() => {
                      navigate('/profile');
                      toggleMobileMenu();
                    }}
                    className="flex items-center text-white font-semibold bg-purple-secondary/70 hover:bg-purple-tertiary/30 w-full py-3 px-4 rounded-md shadow-md"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <UserCircle className="w-6 h-6 text-white" />
                    <span className="ml-3 text-xl">My Profile</span>
                  </button>
                  
                  {isGuest && (
                    <div className="flex flex-col items-start space-y-4 w-full px-4 py-2">
                      <span className="px-3 py-1 bg-amber-500 text-white rounded-full text-sm font-bold">
                        Guest Mode
                      </span>
                      <button 
                        onClick={() => {
                          convertGuestToFull();
                          toggleMobileMenu();
                        }}
                        className="flex items-center text-white font-semibold bg-purple-secondary/70 hover:bg-purple-tertiary/30 w-full py-3 px-4 rounded-md shadow-md"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <User className="w-6 h-6 text-white" />
                        <span className="ml-3 text-xl">Upgrade to Full Account</span>
                      </button>
                    </div>
                  )}
                  
                  <button 
                    onClick={() => {
                      handleSignOut();
                      toggleMobileMenu();
                    }}
                    className="flex items-center text-white font-semibold px-4 py-3 rounded-md hover:bg-purple-tertiary/30 bg-purple-secondary/70 shadow-md"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <LogOut className="w-6 h-6 text-white" />
                    <span className="ml-3 text-xl">Sign Out</span>
                  </button>
                </>
              ) : (
                <a
                  href="/auth"
                  className="flex items-center text-white text-xl font-semibold bg-purple-DEFAULT/50 px-6 py-3 rounded-md hover:bg-purple-DEFAULT/80 shadow-md"
                  onClick={() => toggleMobileMenu()}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <User className="w-6 h-6 mr-2 text-white" />
                  Sign In
                </a>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;
